import type { AgentConfirmEvent } from '@travel-planner/shared/schemas';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { pickCss } from '@/shared/lib/css-utils';
import { Button, Text } from '@/shared/ui';
import css from './agent-confirm-card.module.scss';

const rootCss = pickCss(css, 'root');

export type AgentConfirmCardProps = {
  event: AgentConfirmEvent;
  onApprove: () => void;
  onReject: () => void;
  // Set once the consultant has decided — the buttons lock so the loop can't be
  // double-resolved while the mutation is in flight (the gate is one-shot
  // server-side, but disabling avoids a confusing second click).
  disabled?: boolean;
};

// Confirm card for a `requiresConfirm` tool that paused the loop (P5.1). The
// run is suspended server-side until the consultant approves or rejects; the
// decision rides the `agentConfirm` mutation to resolve the gate. Styling is
// driven by `severity`: a destructive content edit vs a non-deterministic
// generation.
export const AgentConfirmCard = ({
  event,
  onApprove,
  onReject,
  disabled = false,
}: AgentConfirmCardProps) => {
  const isDestructive = event.severity === 'destructive';

  return (
    <fieldset
      className={rootCss({ is: event.severity })}
      aria-label="Confirmation required"
    >
      <div className={css.header}>
        {isDestructive ? (
          <AlertTriangle aria-hidden="true" className={css.icon} size={14} />
        ) : (
          <Sparkles aria-hidden="true" className={css.icon} size={14} />
        )}
        <Text as="span" size="sm" className={css.summary}>
          {event.summary}
        </Text>
      </div>
      <div className={css.actions}>
        <Button
          disabled={disabled}
          onClick={onReject}
          size="sm"
          variant="ghost"
        >
          Cancel
        </Button>
        <Button
          disabled={disabled}
          onClick={onApprove}
          size="sm"
          variant={isDestructive ? 'destructive' : 'primary'}
        >
          {isDestructive ? 'Confirm' : 'Generate'}
        </Button>
      </div>
    </fieldset>
  );
};
